import { registerActions } from "../actionRegistry";
import { api } from "../api";
import { showLoadingOverlay, hideLoadingOverlay, toastError, toastSuccess } from "../ui/messages";
import { modal, closeModalLast, closeModalLastRefresh } from "../ui/modal";
import { getDomElement, queryAll } from "../utils/dom";

/**
 * Everything the edit-mockup form holds. EditMockupModal renders these fields,
 * so it owns them — this used to be 24 `getDomElement(...)` reads out of a modal
 * that had already rendered the values it was being asked for.
 */
export interface MockupFormValues {
  name: string;
  shortname: string;
  keywords: string;
  advancedSettings: string;
  offsetX: string;
  offsetY: string;
  artworkId: string;
  interiorType: string;
  aspect: string;
  productType: string;
  /** Reseller id, or "" for all resellers. */
  partner: string;
  canvas: string;
  canvasSide: string;
  canvasReflection: string;
  canvasMirror: boolean;
  /** 0 = none, 1 = left, 2 = right. */
  side: 0 | 1 | 2;
  isDefault: boolean;
  metadata: boolean;
  api: boolean;
  active: boolean;
  onlyResellers: boolean;
  shadows: number;
  highlights: number;
  reflections: number;
}

/** Which manageinteriors list to return to, mirroring the legacy `$interior_view`. */
function mockupView(values: MockupFormValues): string {
  const scoped = values.partner !== "";
  if (values.interiorType === "1") return scoped ? "reseller-specific" : "for-all";
  return scoped ? "product-reseller-specific" : "product-for-all";
}

export async function saveMockup(
  id: string,
  values: MockupFormValues,
  onSuccess?: () => void
): Promise<void> {
  showLoadingOverlay("Saving interior...");

  try {
    await api.mockups.save({
      id,
      keywords: values.keywords,
      name: values.name,
      shortname: values.shortname,
      default: values.isDefault,
      partner: values.partner,
      canvas: values.canvas,
      canvas_reflection: values.canvasReflection,
      canvas_side: values.canvasSide,
      active: values.active,
      metadata: values.metadata,
      api: values.api,
      shadows: String(values.shadows),
      highlights: String(values.highlights),
      reflections: String(values.reflections),
      aspect: values.aspect,
      interior_type: values.interiorType,
      product_type: values.productType,
      artwork_id: values.artworkId,
      offset_x: values.offsetX,
      offset_y: values.offsetY,
      second_position: values.side,
      canvas_mirror: values.canvasMirror,
      only_resellers: values.onlyResellers,
      adv: values.advancedSettings,
    });
    if (onSuccess) {
      hideLoadingOverlay();
      onSuccess();
    } else {
      location.href = `/manageinteriors/${mockupView(values)}/${id}`;
    }
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

/**
 * The artist-owned mockup form, held by EditUserMockupModal.
 *
 * This function used to read `interior_name`, `adj_shadows`, `side_left` … out of
 * the document — ids that belong to the ADMIN modal (EditMockupModal). The modal
 * that calls it renders `eiu-*` ids, a completely disjoint set, so every field it
 * sent was `undefined` and saving an artist's mockup wrote nothing. The ids
 * existed somewhere in the app, which is exactly why check:dom-ids could not see
 * it: that check proves an id is emitted, not that it is emitted by the component
 * doing the reading.
 */
export interface UserMockupFormValues {
  /** Round-tripped, not edited: the API overwrites it, and "" breaks the preview. */
  filename: string;
  name: string;
  shortname: string;
  aspect: string;
  canvas: string;
  canvasMirror: boolean;
  secondPosition: boolean;
  shadows: number;
  highlights: number;
  offsetX: string;
  offsetY: string;
  advancedSettings: string;
}

export async function saveUserMockup(id: string, values: UserMockupFormValues): Promise<void> {
  showLoadingOverlay("Saving interior...");

  try {
    await api.mockups.saveUser({
      id,
      filename: values.filename,
      name: values.name,
      shortname: values.shortname,
      canvas: values.canvas,
      shadows: values.shadows,
      highlights: values.highlights,
      offset_x: Number(values.offsetX) || 0,
      offset_y: Number(values.offsetY) || 0,
      second_position: values.secondPosition ? 1 : 0,
      canvas_mirror: values.canvasMirror,
      adv: values.advancedSettings,
      aspect: values.aspect,
    });
    hideLoadingOverlay();
    toastSuccess("Interior saved");
    closeModalLastRefresh();
  } catch (err: unknown) {
    hideLoadingOverlay();
    toastError((err as Error).message);
  }
}

export async function deleteMockup(id: string): Promise<void> {
  const defaultEl = getDomElement<HTMLInputElement>("interior_default");
  if (defaultEl?.checked) {
    alert(
      "You cannot delete this interior since it is marked as default. Select another interior as default for this aspect before you delete this one."
    );
    return;
  }
  if (!confirm("Are you sure you want to remove the interior?")) return;
  try {
    await api.mockups.delete({ id });
    location.href = "/manageinteriors";
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function saveMockupOrder(aspect: string, _mode: string): Promise<void> {
  const i_order: string[] = [];
  queryAll("img.reorderinterior").forEach((el) => {
    const raw_id = el.id.replace("interior-", "");
    if (raw_id) {
      i_order.push(raw_id);
    }
  });

  try {
    await api.mockups.saveOrder({ aspect: aspect as "p" | "s" | "l", order: i_order });
    location.href = "/manageinteriors";
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

/**
 * Save the canvas coordinates from UploadUserMockupModal.
 *
 * This read `interior_canvas` / `interior_canvas_side` — the ADMIN modal's ids,
 * with underscores. The calling modal renders `interior-canvas` /
 * `interior-canvas-side`, with hyphens. So the lookup was always null and the
 * function bailed with "You must enter the canvas parameters" **even when the
 * fields were filled in**: an artist could never save a mockup's canvas. Same
 * shape as the saveUserMockup bug — see todo/mockup-flow-followups.md.
 */
export async function saveMockupCanvas(
  intid: string,
  values: { canvas: string; canvasSide: string }
): Promise<void> {
  if (!values.canvas) {
    toastError("You must enter the canvas parameters");
    return;
  }

  try {
    await api.mockups.saveCanvas({
      canvas: values.canvas,
      canvas_side: values.canvasSide,
      id: intid,
    });
    closeModalLast();
    setTimeout(() => {
      modal("editinterioruser:" + intid, "large");
    }, 300);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  saveinterior: saveMockup,
  saveinterioruser: saveUserMockup,
  deleteinterior: deleteMockup,
  saveinteriororder: saveMockupOrder,
  savecanvas: saveMockupCanvas,
});
